'use client';

import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import TrackingForm from './TrackingForm';
import ShipmentDetails from './ShipmentDetails';
import TrackingTimeline from './TrackingTimeline';
import Icon from '@/components/ui/AppIcon';
import LiveMapTracking from './LiveMapTracking';
import PaymentForm from './PaymentForm';
import { useRealtimeShipments, useRealtimeTrackingEvents, useRealtimeDestinationStops } from '@/hooks/useRealtimeShipments';
import { createClient } from '@/lib/supabase/client';




export default function TrackShipmentInteractive() {
  const searchParams = useSearchParams();
  const initialTracking = searchParams.get('tracking') || '';
  const supabase = createClient();
  
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedShipmentId, setSelectedShipmentId] = useState<string | null>(null);
  const [trackingInput, setTrackingInput] = useState(initialTracking);
  const [shipmentData, setShipmentData] = useState<any>(null);

  // Get authenticated user
  useEffect(() => {
    const getUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id || null);
    };
    getUser();
  }, []);

  // Real-time hooks
  const { shipments } = useRealtimeShipments(userId);
  const { events } = useRealtimeTrackingEvents(selectedShipmentId);
  const { stops } = useRealtimeDestinationStops(selectedShipmentId);

  // Handle tracking search
  useEffect(() => {
    if (trackingInput && shipments?.length > 0) {
      const foundShipment = shipments.find(
        (s) => s.trackingNumber.toLowerCase() === trackingInput.toLowerCase()
      );
      
      if (foundShipment) {
        setSelectedShipmentId(foundShipment.id);
        setShipmentData({
          trackingNumber: foundShipment.trackingNumber,
          status: foundShipment.status.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          estimatedDelivery: foundShipment.estimatedDelivery
            ? new Date(foundShipment.estimatedDelivery).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : 'TBD',
          origin: foundShipment.origin,
          destination: foundShipment.destination,
          weight: foundShipment.weight || 'N/A',
          dimensions: foundShipment.dimensions || 'N/A',
          destinationStops: stops?.map((stop) => ({
            id: stop.id,
            location: stop.location,
            city: stop.city,
            country: stop.country,
            countryCode: stop.countryCode,
            stopType: stop.stopType,
            completed: stop.completed,
          })) || [],
          events: events?.map((event) => ({
            id: event.id,
            status: event.status,
            location: event.location,
            countryCode: event.countryCode,
            timestamp: new Date(event.timestamp).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }),
            description: event.description,
            completed: event.completed,
          })) || [],
        });
      } else {
        setShipmentData({
          error: true,
          message: 'Tracking number not found. Please check and try again.',
        });
      }
    }
  }, [trackingInput, shipments, events, stops]);

  const handleTrack = (trackingNumber: string) => {
    setTrackingInput(trackingNumber);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="grid lg:grid-cols-3 gap-8">
        {/* Left Column - Tracking Form */}
        <div className="lg:col-span-1">
          <TrackingForm onTrack={handleTrack} />
        </div>

        {/* Right Column - Results */}
        <div className="lg:col-span-2">
          {shipmentData ? (
            shipmentData.error ? (
              <div className="bg-error/10 border border-error/20 rounded-xl p-8 text-center">
                <Icon name="ExclamationCircleIcon" size={48} className="text-error mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">Tracking Number Not Found</h3>
                <p className="text-muted-foreground">{shipmentData.message}</p>
              </div>
            ) : (
              <div className="space-y-6">
                <ShipmentDetails
                  trackingNumber={shipmentData.trackingNumber}
                  status={shipmentData.status}
                  estimatedDelivery={shipmentData.estimatedDelivery}
                  origin={shipmentData.origin}
                  destination={shipmentData.destination}
                  weight={shipmentData.weight}
                  dimensions={shipmentData.dimensions}
                />
                
                {/* Destination Stops Map */}
                {shipmentData?.destinationStops && shipmentData.destinationStops.length > 0 && (
                  <div className="bg-card rounded-xl p-6 shadow-card border border-border">
                    <h3 className="text-xl font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
                      <Icon name="MapIcon" size={24} className="text-primary" />
                      Destination Stop Points ({shipmentData.destinationStops.length})
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {shipmentData.destinationStops.map((stop: any, index: number) => {
                        const stopCompleted = shipmentData.events.some(
                          (event: any) => event.location.includes(stop.location) && event.completed
                        );
                        return (
                          <div
                            key={stop.id}
                            className={`p-4 rounded-lg border-2 transition-all ${
                              stopCompleted
                                ? 'bg-success/5 border-success' :'bg-muted border-border'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                  stopCompleted
                                    ? 'bg-success text-success-foreground'
                                    : 'bg-muted-foreground/20 text-muted-foreground'
                                }`}
                              >
                                {index + 1}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-semibold text-foreground text-sm truncate">
                                    {stop.location}
                                  </h4>
                                  {stopCompleted && (
                                    <Icon name="CheckCircleIcon" size={16} className="text-success shrink-0" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  {stop.city}, {stop.country}
                                </p>
                                <span
                                  className={`inline-block mt-2 px-2 py-1 rounded text-xs font-semibold ${
                                    stopCompleted
                                      ? 'bg-success/20 text-success' :'bg-muted-foreground/10 text-muted-foreground'
                                  }`}
                                >
                                  {stop.countryCode}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                <div className="bg-card rounded-xl p-6 shadow-card border border-border">
                  <h3 className="text-xl font-heading font-semibold text-foreground mb-6">Tracking History</h3>
                  <TrackingTimeline events={shipmentData.events} />
                </div>
              </div>
            )
          ) : (
            <div className="bg-muted rounded-xl p-12 text-center">
              <Icon name="MagnifyingGlassIcon" size={64} className="text-muted-foreground mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">Enter Tracking Number</h3>
              <p className="text-muted-foreground">Enter your tracking number to see shipment details and live tracking updates</p>
            </div>
          )}
        </div>
      </div>

      {/* Live Map Tracking */}
      {shipmentData && shipmentData?.destinationStops && shipmentData.destinationStops.length > 0 && (
        <LiveMapTracking
          locations={shipmentData.destinationStops.map((stop: any, index: number) => {
            const stopCompleted = shipmentData.events.some(
              (event: any) => event.location.includes(stop.location) && event.completed
            );
            // Mock coordinates for demonstration - in production, use geocoding API
            const mockCoordinates = [
              { lat: 40.7128, lng: -74.0060 }, // New York
              { lat: 40.6413, lng: -73.7781 }, // JFK
              { lat: 51.4700, lng: -0.4543 }, // Heathrow
              { lat: 51.5074, lng: -0.1278 }, // London Hub
              { lat: 51.5194, lng: -0.1270 }, // Baker Street
            ];
            return {
              lat: mockCoordinates[index]?.lat || 40.7128,
              lng: mockCoordinates[index]?.lng || -74.0060,
              label: stop.location,
              type: stop.stopType,
              completed: stopCompleted,
            };
          })}
          currentLocation={{
            lat: 51.4700,
            lng: -0.4543,
            label: 'Current Location',
            type: 'current',
            completed: true,
          }}
        />
      )}

      {/* Payment Form */}
      {shipmentData && !shipmentData.error && (
      <PaymentForm
        orderDetails={{
          trackingNumber: shipmentData.trackingNumber,
          serviceType: 'Air Freight',
          weight: shipmentData.weight,
          dimensions: shipmentData.dimensions,
          origin: shipmentData.origin,
          destination: shipmentData.destination,
          estimatedDelivery: shipmentData.estimatedDelivery,
        }}
        totalAmount={299.99}
        onPaymentComplete={(paymentData) => {
          console.log('Payment completed:', paymentData);
        }}
      />
      )}
    </div>
  );
}